"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.clusterViewport = exports.stackMembers = exports.focusAccumulator = exports.viewportAccumulator = exports.snapViewportToTiles = exports.renderPosition = exports.expansionZoom = exports.bboxTileCount = exports.tileRange = exports.formatBbox = exports.parseBbox = exports.groupKey = exports.cellKey = exports.cellOf = exports.fromWorld = exports.toWorldY = exports.toWorldX = exports.WORLD_BBOX = exports.MAX_LAT = exports.MAX_RESPONSE_ENTRIES = exports.MAX_BBOX_TILES = exports.CLUSTER_ICON_PX = exports.CLUSTER_CELL_PX = exports.TILE_PX = exports.UNCLUSTERED_ZOOM = exports.MAX_CLUSTER_ZOOM = exports.MAX_ZOOM = void 0;
const effect_1 = require("effect");
// A point's cell at zoom `z` is the top `z + 2` bits of its u32 world coordinates.
exports.MAX_ZOOM = 22;
exports.MAX_CLUSTER_ZOOM = 16;
exports.UNCLUSTERED_ZOOM = 17;
exports.TILE_PX = 256;
exports.CLUSTER_CELL_PX = 64;
exports.CLUSTER_ICON_PX = 48;
exports.MAX_BBOX_TILES = 256;
exports.MAX_RESPONSE_ENTRIES = 5000;
exports.MAX_LAT = 85.0511287798;
exports.WORLD_BBOX = [-180, -exports.MAX_LAT, 180, exports.MAX_LAT];
const WORLD = 2 ** 32;
const MAX_U32 = WORLD - 1;
const unitsPerPx = (zoom) => 2 ** (32 - Math.log2(exports.TILE_PX) - zoom);
const clampU32 = (v) => (Number.isNaN(v) ? 0 : Math.min(MAX_U32, Math.max(0, v)));
const toWorldX = (lon) => clampU32(Math.floor(((lon + 180) / 360) * WORLD));
exports.toWorldX = toWorldX;
const toWorldY = (lat) => {
    const s = Math.sin((Math.min(exports.MAX_LAT, Math.max(-exports.MAX_LAT, lat)) * Math.PI) / 180);
    return clampU32(Math.floor((0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * WORLD));
};
exports.toWorldY = toWorldY;
const lonOfWorld = (x) => (x / WORLD) * 360 - 180;
const latOfWorld = (y) => (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / WORLD))) * 180) / Math.PI;
const fromWorld = (x, y) => [
    latOfWorld(y),
    lonOfWorld(x),
];
exports.fromWorld = fromWorld;
const cellShift = (zoom) => 30 - Math.min(zoom, exports.MAX_CLUSTER_ZOOM);
const cellOf = (x, y, zoom) => {
    const shift = cellShift(zoom);
    return [x >>> shift, y >>> shift];
};
exports.cellOf = cellOf;
const cellKey = (x, y, zoom) => {
    const shift = cellShift(zoom);
    return (x >>> shift) * 2 ** (32 - shift) + (y >>> shift);
};
exports.cellKey = cellKey;
const groupKey = (x, y, zoom) => zoom >= exports.UNCLUSTERED_ZOOM ? `${x},${y}` : `${(0, exports.cellKey)(x, y, zoom)}`;
exports.groupKey = groupKey;
const DECIMAL = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;
const parseDecimal = (s) => {
    const trimmed = s.trim();
    return DECIMAL.test(trimmed) ? Number(trimmed) : Number.NaN;
};
const isLon = (v) => Number.isFinite(v) && v >= -180 && v <= 180;
const isLat = (v) => Number.isFinite(v) && v >= -90 && v <= 90;
// `west > east` is a box that crosses the antimeridian.
const parseBbox = (s) => {
    const parts = s.split(',');
    if (parts.length !== 4)
        return effect_1.Option.none();
    const [west = Number.NaN, south = Number.NaN, east = Number.NaN, north = Number.NaN] = parts.map(parseDecimal);
    return isLon(west) && isLon(east) && isLat(south) && isLat(north) && south <= north
        ? effect_1.Option.some([west, south, east, north])
        : effect_1.Option.none();
};
exports.parseBbox = parseBbox;
const SCALE = 1e6;
const formatScaled = (k) => {
    const abs = Math.abs(k);
    return `${k < 0 ? '-' : ''}${Math.floor(abs / SCALE)}.${String(abs % SCALE).padStart(6, '0')}`;
};
// `v * SCALE` can be one unit off either way, so the candidate is checked against `v` itself.
const floorScaled = (v) => {
    const k = Math.floor(v * SCALE);
    if (k / SCALE > v)
        return k - 1;
    return (k + 1) / SCALE <= v ? k + 1 : k;
};
const ceilScaled = (v) => {
    const k = Math.ceil(v * SCALE);
    if (k / SCALE < v)
        return k + 1;
    return (k - 1) / SCALE >= v ? k - 1 : k;
};
const formatBbox = ([west, south, east, north]) => [floorScaled(west), floorScaled(south), ceilScaled(east), ceilScaled(north)]
    .map(formatScaled)
    .join(',');
exports.formatBbox = formatBbox;
const tileRange = ([west, south, east, north], zoom) => {
    const size = 2 ** (32 - zoom);
    const tiles = 2 ** zoom;
    const x0 = Math.floor((0, exports.toWorldX)(west) / size);
    const x1 = Math.floor((0, exports.toWorldX)(east) / size);
    return {
        tiles,
        x0,
        columns: west <= east ? x1 - x0 + 1 : Math.min(tiles, tiles - x0 + x1 + 1),
        y0: Math.floor((0, exports.toWorldY)(north) / size),
        y1: Math.floor((0, exports.toWorldY)(south) / size),
    };
};
exports.tileRange = tileRange;
const bboxTileCount = (bbox, zoom) => {
    const { columns, y0, y1 } = (0, exports.tileRange)(bbox, zoom);
    return columns * (y1 - y0 + 1);
};
exports.bboxTileCount = bboxTileCount;
const expansionZoom = (minX, maxX, minY, maxY) => {
    const commonBits = Math.min(Math.clz32(minX ^ maxX), Math.clz32(minY ^ maxY));
    return commonBits === 32 ? null : Math.max(0, Math.min(commonBits - 1, exports.UNCLUSTERED_ZOOM));
};
exports.expansionZoom = expansionZoom;
const renderPosition = (lat, lon, zoom) => {
    if (zoom >= exports.UNCLUSTERED_ZOOM)
        return { lat, lng: lon };
    const shift = cellShift(zoom);
    const cell = 2 ** shift;
    const inset = (exports.CLUSTER_ICON_PX / 2) * unitsPerPx(zoom);
    const clamp = (v) => {
        const start = Math.floor(v / cell) * cell;
        return Math.min(Math.max(v, start + inset), start + cell - 1 - inset);
    };
    const x = (0, exports.toWorldX)(lon);
    const y = (0, exports.toWorldY)(lat);
    const cx = clamp(x);
    const cy = clamp(y);
    // Half a unit keeps the point inside its unit after the projection round trip.
    return {
        lat: cy === y ? lat : latOfWorld(cy + 0.5),
        lng: cx === x ? lon : lonOfWorld(cx + 0.5),
    };
};
exports.renderPosition = renderPosition;
// Above the up to ~140 world units `formatBbox` rounding moves an edge, below one 64 px cell.
const SNAP_INSET_PX = 48;
const wrapLon = (lon) => lon >= -180 && lon <= 180 ? lon : ((((lon + 180) % 360) + 360) % 360) - 180;
const snapViewportToTiles = (visible, zoom, padRatio) => {
    if (zoom <= 3)
        return exports.WORLD_BBOX;
    const [west, south, east, north] = visible;
    const size = 2 ** (32 - zoom);
    const tiles = 2 ** zoom;
    const inset = SNAP_INSET_PX * unitsPerPx(zoom);
    const tileOf = (v) => Math.floor(v / size);
    const latPad = (north - south) * padRatio;
    const ty0 = tileOf((0, exports.toWorldY)(Math.min(exports.MAX_LAT, north + latPad)));
    const ty1 = tileOf((0, exports.toWorldY)(Math.max(-exports.MAX_LAT, south - latPad)));
    const lonSpan = west <= east ? east - west : east - west + 360;
    const lonPad = lonSpan * padRatio;
    let tx0 = 0;
    let tx1 = tiles - 1;
    if (lonSpan + 2 * lonPad < 360) {
        const w = wrapLon(west - lonPad);
        const e = wrapLon(east + lonPad);
        const wx0 = tileOf((0, exports.toWorldX)(w));
        const wx1 = tileOf((0, exports.toWorldX)(e));
        // A box that wraps the antimeridian until its halves meet covers every column.
        if (w <= e || wx1 + 1 < wx0) {
            tx0 = wx0;
            tx1 = wx1;
        }
    }
    return [
        tx0 === 0 ? -180 : lonOfWorld(tx0 * size + inset),
        ty1 === tiles - 1 ? -exports.MAX_LAT : latOfWorld((ty1 + 1) * size - 1 - inset),
        tx1 === tiles - 1 ? 180 : lonOfWorld((tx1 + 1) * size - 1 - inset),
        ty0 === 0 ? exports.MAX_LAT : latOfWorld(ty0 * size + inset),
    ];
};
exports.snapViewportToTiles = snapViewportToTiles;
const DENSE_CELL_LIMIT = 1 << 14;
// Every added point counts towards `total` and `bounds`; the bbox is tested on whole cells.
const viewportAccumulator = (points, zoom, bbox) => {
    const { x: xs, y: ys, lat: lats, lon: lons } = points;
    const [west, south, east, north] = bbox;
    const shift = cellShift(zoom);
    const cellSize = 2 ** shift;
    const offsetMask = cellSize - 1;
    const cellsPerAxis = 2 ** (32 - shift);
    const exact = zoom >= exports.UNCLUSTERED_ZOOM;
    const cx0 = (0, exports.toWorldX)(west) >>> shift;
    const cx1 = (0, exports.toWorldX)(east) >>> shift;
    const cy0 = (0, exports.toWorldY)(north) >>> shift;
    const rows = ((0, exports.toWorldY)(south) >>> shift) - cy0 + 1;
    const wraps = west > east;
    const wrapsFully = wraps && cx1 + 1 >= cx0;
    const col0 = wrapsFully ? 0 : cx0;
    const partialCols = wraps ? cellsPerAxis - cx0 + cx1 + 1 : cx1 - cx0 + 1;
    const cols = wrapsFully ? cellsPerAxis : partialCols;
    const denseCells = exact || cols * rows > DENSE_CELL_LIMIT ? 0 : cols * rows;
    const count = new Uint32Array(denseCells);
    const first = new Uint32Array(denseCells);
    const worstRank = new Uint8Array(denseCells);
    const minXs = new Uint32Array(denseCells);
    const maxXs = new Uint32Array(denseCells);
    const minYs = new Uint32Array(denseCells);
    const maxYs = new Uint32Array(denseCells);
    const dxs = new Float64Array(denseCells);
    const dys = new Float64Array(denseCells);
    const sparse = new Map();
    let total = 0;
    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;
    const add = (index, rank) => {
        const lat = lats[index] ?? Number.NaN;
        const lon = lons[index] ?? Number.NaN;
        total++;
        if (lon < minLon)
            minLon = lon;
        if (lon > maxLon)
            maxLon = lon;
        if (lat < minLat)
            minLat = lat;
        if (lat > maxLat)
            maxLat = lat;
        const x = xs[index] ?? 0;
        const y = ys[index] ?? 0;
        const cx = x >>> shift;
        const col = cx >= col0 ? cx - col0 : cx - col0 + cellsPerAxis;
        const row = (y >>> shift) - cy0;
        if (col >= cols || row < 0 || row >= rows)
            return;
        if (denseCells > 0) {
            const c = col * rows + row;
            const k = count[c] ?? 0;
            count[c] = k + 1;
            dxs[c] = (dxs[c] ?? 0) + (x & offsetMask);
            dys[c] = (dys[c] ?? 0) + (y & offsetMask);
            if (k === 0) {
                first[c] = index;
                worstRank[c] = rank;
                minXs[c] = maxXs[c] = x;
                minYs[c] = maxYs[c] = y;
                return;
            }
            if (rank > (worstRank[c] ?? 0))
                worstRank[c] = rank;
            minXs[c] = Math.min(minXs[c] ?? 0, x);
            maxXs[c] = Math.max(maxXs[c] ?? 0, x);
            minYs[c] = Math.min(minYs[c] ?? 0, y);
            maxYs[c] = Math.max(maxYs[c] ?? 0, y);
            return;
        }
        const key = exact ? `${x},${y}` : cx * cellsPerAxis + (y >>> shift);
        const group = sparse.get(key);
        if (group === undefined) {
            sparse.set(key, {
                first: index,
                count: 1,
                x0: x,
                y0: y,
                dx: 0,
                dy: 0,
                minX: x,
                maxX: x,
                minY: y,
                maxY: y,
                rank,
            });
            return;
        }
        group.count++;
        group.dx += x - group.x0;
        group.dy += y - group.y0;
        if (rank > group.rank)
            group.rank = rank;
        group.minX = Math.min(group.minX, x);
        group.maxX = Math.max(group.maxX, x);
        group.minY = Math.min(group.minY, y);
        group.maxY = Math.max(group.maxY, y);
    };
    // Cell-key order: the columns past the antimeridian (cx from 0) come first.
    const denseGroups = () => {
        const groups = [];
        const wrapAt = Math.min(cellsPerAxis - col0, cols);
        for (const [from, to] of [
            [wrapAt, cols],
            [0, wrapAt],
        ]) {
            for (let col = from; col < to; col++) {
                const x0 = ((col0 + col) % cellsPerAxis) * cellSize;
                for (let row = 0; row < rows; row++) {
                    const c = col * rows + row;
                    const k = count[c] ?? 0;
                    if (k === 0)
                        continue;
                    groups.push({
                        first: first[c] ?? 0,
                        count: k,
                        x0,
                        y0: (cy0 + row) * cellSize,
                        dx: dxs[c] ?? 0,
                        dy: dys[c] ?? 0,
                        minX: minXs[c] ?? 0,
                        maxX: maxXs[c] ?? 0,
                        minY: minYs[c] ?? 0,
                        maxY: maxYs[c] ?? 0,
                        rank: (worstRank[c] ?? 0),
                    });
                }
            }
        }
        return groups;
    };
    const sparseGroups = () => exact
        ? Array.from(sparse.values()).sort((a, b) => a.x0 - b.x0 || a.y0 - b.y0)
        : Array.from(sparse.entries())
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([, group]) => group);
    const finish = () => {
        const groups = denseCells > 0 ? denseGroups() : sparseGroups();
        const clusters = [];
        const singles = [];
        for (const group of groups.slice(0, exports.MAX_RESPONSE_ENTRIES)) {
            if (group.count === 1) {
                singles.push(group.first);
                continue;
            }
            const ez = (0, exports.expansionZoom)(group.minX, group.maxX, group.minY, group.maxY);
            // Half a unit keeps the centroid inside its cell after the projection round trip.
            const [lat, lon] = ez === null
                ? [lats[group.first] ?? Number.NaN, lons[group.first] ?? Number.NaN]
                : (0, exports.fromWorld)(group.x0 + group.dx / group.count + 0.5, group.y0 + group.dy / group.count + 0.5);
            clusters.push([lat, lon, group.count, group.rank, ez]);
        }
        return {
            clusters,
            singles,
            truncated: groups.length > exports.MAX_RESPONSE_ENTRIES,
            total,
            bounds: total === 0 ? null : [minLon, minLat, maxLon, maxLat],
        };
    };
    return { add, finish };
};
exports.viewportAccumulator = viewportAccumulator;
const focusAccumulator = (points, target) => {
    const { x: xs, y: ys } = points;
    const xt = xs[target] ?? 0;
    const yt = ys[target] ?? 0;
    let seen = false;
    let stacked = false;
    let commonBits = -Infinity;
    return {
        add: index => {
            if (index === target) {
                seen = true;
                return;
            }
            const x = xs[index] ?? 0;
            const y = ys[index] ?? 0;
            if (x === xt && y === yt) {
                stacked = true;
                return;
            }
            const bits = Math.min(Math.clz32(xt ^ x), Math.clz32(yt ^ y));
            if (bits > commonBits)
                commonBits = bits;
        },
        finish: () => seen ? { zoom: Math.min(exports.UNCLUSTERED_ZOOM, Math.max(0, commonBits - 1)), stacked } : null,
    };
};
exports.focusAccumulator = focusAccumulator;
const stackMembers = (points, selection, lat, lon) => {
    const x = (0, exports.toWorldX)(lon);
    const y = (0, exports.toWorldY)(lat);
    const members = [];
    for (const index of selection) {
        if (points.x[index] === x && points.y[index] === y)
            members.push(index);
    }
    return members;
};
exports.stackMembers = stackMembers;
const clusterViewport = (points, selection, ranks, zoom, bbox) => {
    const accumulator = (0, exports.viewportAccumulator)(points, zoom, bbox);
    for (const index of selection)
        accumulator.add(index, ranks[index] ?? 0);
    return accumulator.finish();
};
exports.clusterViewport = clusterViewport;
//# sourceMappingURL=grid.js.map